import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Palette, Brain, Target, Lightbulb, Loader } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import focusService from '../../services/focusService';
import { supabase } from '../../lib/supabase';

// Default icons for focuses (cycle through these)
const DEFAULT_ICONS = [Target, Compass, Lightbulb, Palette, Brain];

// Calculate activity text from last_active_at
function getActivityText(lastActiveAt) {
  if (!lastActiveAt) return 'Not yet active';
  
  const now = new Date();
  const lastActive = new Date(lastActiveAt);
  const diffMs = now - lastActive;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  if (diffMinutes < 60) return 'Active just now';
  if (diffHours < 24) return `Active ${diffHours}h ago`;
  if (diffDays === 0) return 'Active today';
  if (diffDays === 1) return 'Active yesterday';
  if (diffDays < 7) return `Active ${diffDays} days ago`;
  if (diffDays < 14) return `Active ${Math.floor(diffDays / 7)} week ago`;
  return `Active ${Math.floor(diffDays / 7)} weeks ago`;
}

export default function Focuses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [focuses, setFocuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadFocuses();
    }
  }, [user?.id]);

  const loadFocuses = async () => {
    try {
      setLoading(true);
      
      // Get all focuses
      const allFocuses = await focusService.getUserFocuses(user.id);
      
      // For each focus, calculate last_active_at from multiple sources
      const focusesWithActivity = await Promise.all(
        allFocuses.map(async (focus) => {
          let lastActiveAt = focus.last_active_at;
          
          // If no last_active_at, check conversations
          if (!lastActiveAt) {
            const { data: conversations } = await supabase
              .schema('public')
              .from('conversations')
              .select('updated_at')
              .eq('focus_id', focus.id)
              .order('updated_at', { ascending: false })
              .limit(1)
              .single();
            
            if (conversations?.updated_at) {
              lastActiveAt = conversations.updated_at;
            }
          }
          
          return {
            ...focus,
            calculated_last_active_at: lastActiveAt
          };
        })
      );
      
      // Sort by most recent activity (nulls last)
      const sorted = focusesWithActivity.sort((a, b) => {
        const aTime = a.calculated_last_active_at ? new Date(a.calculated_last_active_at).getTime() : 0;
        const bTime = b.calculated_last_active_at ? new Date(b.calculated_last_active_at).getTime() : 0;
        return bTime - aTime;
      });
      
      // Take top 3 most recent
      setFocuses(sorted.slice(0, 3));
    } catch (error) {
      console.error('[Focuses] Error loading:', error);
      setFocuses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFocusClick = (focusId) => {
    navigate(`/focus/${focusId}`);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[22px] p-6" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
        <h3 className="text-base font-semibold mb-5 tracking-tight" style={{ color: '#111111' }}>Recent Focuses</h3>
        <div className="flex items-center justify-center py-8">
          <Loader className="animate-spin" size={24} style={{ color: '#7C3AED' }} />
        </div>
      </div>
    );
  }

  if (focuses.length === 0) {
    return null; // Hide section if no focuses
  }

  return (
    <div className="bg-white rounded-[22px] p-6" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
      <h3 className="text-base font-semibold mb-5 tracking-tight" style={{ color: '#111111' }}>Recent Focuses</h3>
      <div className="grid grid-cols-3 gap-3">
        {focuses.map((focus, index) => {
          const Icon = DEFAULT_ICONS[index % DEFAULT_ICONS.length];
          const activityText = getActivityText(focus.calculated_last_active_at);
          
          return (
            <div 
              key={focus.id}
              onClick={() => handleFocusClick(focus.id)}
              className="group relative flex flex-col items-center gap-2.5 p-4 rounded-[20px] cursor-pointer transition-all duration-200 text-center bg-white"
              style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#7C3AED] opacity-0 group-hover:opacity-100 transition-opacity rounded-l-[20px]"></div>
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(136, 92, 255, 0.06)' }}
              >
                <Icon size={18} style={{ color: '#7C3AED' }} />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <h4 className="text-[12px] font-semibold leading-tight mb-1" style={{ color: '#111111' }}>
                  {focus.title}
                </h4>
                <p className="text-[11px] mt-0.5 leading-tight" style={{ color: '#999999' }}>
                  {activityText}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
